import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// formatDueDate + daysUntil moved to src/lib/dates.ts (PR-2): the old
// instant-based daysUntil under-counted a day after ~18:00 Boise; the
// replacement measures calendar-date distance in the user's resolved timezone.

/**
 * Max image upload size for profile avatars and org logos (which share the
 * `avatars` storage bucket). One source of truth so the two upload sites
 * (Profile, Settings) and their helper text never drift from each other — and
 * must stay in sync with the bucket's file_size_limit (migration 090).
 */
export const MAX_IMAGE_UPLOAD_BYTES = 2 * 1024 * 1024
export const MAX_IMAGE_UPLOAD_LABEL = '2 MB'

/**
 * AI Impact estimate — the single source of truth for the "hours saved" metric
 * surfaced on both the Command Center AI Impact card and the Analytics page.
 * We model each AI action as saving ~5 minutes of human work; hours are rounded
 * to the nearest whole hour. Keeping the per-action constant here prevents the
 * two cards from silently diverging if the estimate is ever tuned.
 */
export const MINUTES_SAVED_PER_AI_ACTION = 5

export function estimateHoursSaved(aiCount: number): number {
  return Math.round((aiCount * MINUTES_SAVED_PER_AI_ACTION) / 60)
}

/**
 * CSV cell hardening: quote-double AND neutralize spreadsheet formula
 * injection — lead-supplied values (names, tags) starting with = + - @ or a
 * tab/CR execute as formulas when the export opens in Excel/Sheets; prefixing
 * a single quote renders them as text (the OWASP CSV-injection fix). Shared
 * by the Contacts + reverse-search exports (R4-3 review remediation).
 */
export function csvCell(value: unknown): string {
  let s = String(value ?? '')
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  return `"${s.replace(/"/g, '""')}"`
}
